<template>
  <div
    class="item-combo-select"
    :class="{ 'item-combo-select--no-net': !netChoosable }"
  >
    <input-dropdown
      ref="dropdown"
      :options="allItemsFiltered"
      :searchable="true"
      :placeholder="placeholder"
      :clear-on-select="true"
      :label="label"
      prop="title"
      group-key="type"
      item-key="uid"
      :button-class="netChoosable ? 'item-combo-select__dropdown' : ''"
      @update:value="onDropdownSelect"
    >
      <template #before-options="{ close }">
        <slot name="before-options" :close="close"></slot>
      </template>
      <template #after-options="{ close }">
        <slot name="after-options" :close="close"></slot>
      </template>
      <template #grouplabel="{ value: itemType }">
        <div class="options-list__grouplabel">
          {{ $t(`objectNames.${itemType}`, 3) }}
        </div>
      </template>

      <template #default="{ item }">
        <template v-if="item.type == null">
          <slot name="null-text">
            {{ $t("labels.none") }}
          </slot>
        </template>
        <template v-else>
          {{ item.title }}
        </template>
      </template>

      <template #empty>
        <slot name="empty"></slot>
      </template>
    </input-dropdown>

    <button
      v-if="netChoosable"
      class="button button--clear item-combo-select__button"
      type="button"
      @click="choosing = true"
    >
      <lucide-icon name="select-net-2" />
    </button>

    <net-choosing-overlay
      v-if="choosing"
      :selection="selection"
      :disabled-item-uids="disabledAndUnavailableItems"
      :targets="targets"
      :resources="resources"
      :types="choosableTypes"
      :instruction-text="instructionText"
      :fixed-element="fixedElement"
      @choose="onNetChoose($event as { uid: Uid<T> }[])"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends 'target' | 'resource' | 'index'">
/**
 * This component provides a combined dropdown for targets, resources and indices.
 * The items can also be selected in the net.
 */
import { computed, ref, useTemplateRef } from "vue";
import { filter } from "lodash-es";
import { normalizeIdentity, toUid } from "@/helpers/utility/Id";
import { useI18n } from "vue-i18n";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import NetChoosingOverlay from "@editor/net/NetChoosingOverlay.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import type { Identity, Uid } from "@/helpers/utility/Id";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { ElementItem } from "@editor/items/element/Element";
import { isInList } from "@/helpers/utility/Type";

const props = withDefaults(defineProps<{
  targets: { [targetId: number | string]: TargetItem };
  resources: { [resourceId: number | string]: ResourceItem };
  indices: { [indexId: number | string]: IndexItem };

  availableIds?: {
    target?: number[];
    resource?: number[];
    index: number[];
  } | null;
  placeholder?: string | null;
  instruction?: string;
  selection?: Identity<"target" | "resource" | "index">[];
  hideSelected?: boolean;
  netChoosable?: boolean;
  disabledItemUids?: Set<ElementItem["uid"]>;
  fixedElement?:
    | {
        type: "resource" | "target" | "index";
        id: number;
        uid: ElementItem["uid"];
      }
    | undefined;
  allOptionVisible?: boolean;
  label?: string;
  includeNull?: boolean;
  types?: T[];
}>(), {
  availableIds: null,
  placeholder: null,
  instruction: "",
  selection: () => [],
  hideSelected: false,
  netChoosable: true,
  disabledItemUids: () => new Set(),
  fixedElement: undefined,
  allOptionVisible: false,
  label: "",
  includeNull: false,
  types: () => ["target", "resource", "index"] as T[],
});

const emit = defineEmits<{
  (e: "select", items: { uid: Uid<T> }[]): void;
  (e: "choose", items: Identity<T>[] ): void;
  (e: "select-single", item: Identity<T> | null): void;
}>();

const { t } = useI18n();

const choosing = ref(false);
const dropdown = useTemplateRef("dropdown");

// emits: ["select", "choose", "select-single"],

const targetsOrder = computed(() => {
  return useTargetStore().order;
});
const resourceOrder = computed(() => {
  return useResourceStore().order;
});
const indexOrder = computed(() => {
  return useIndexStore().order;
});
const availableTargets = computed(() => {
  if (!isInList("target", props.types)) return [];
  if (!props.availableIds) {
    return targetsOrder.value.reduce((targets, targetId) => {
      if (props.targets[targetId]) {
        targets.push(props.targets[targetId]);
      }
      return targets;
    }, [] as TargetItem[]);
  }
  if (!props.availableIds.target) return [];
  return props.availableIds.target.map((id: number) => props.targets[id]);
});
const availableResources = computed(() => {
  if (!isInList("resource", props.types)) return [];
  if (!props.availableIds) {
    return resourceOrder.value.reduce((resources, resourceId) => {
      if (props.resources[resourceId]) {
        resources.push(props.resources[resourceId]);
      }
      return resources;
    }, [] as ResourceItem[]);
  }
  if (!props.availableIds.resource) return [];
  return props.availableIds.resource.map((id: number) => props.resources[id]);
});
const availableIndices = computed(() => {
  if (!isInList("index", props.types)) return [];
  if (!props.availableIds) {
    return indexOrder.value.reduce((indices, indexId) => {
      if (props.indices[indexId]) {
        indices.push(props.indices[indexId]);
      }
      return indices;
    }, [] as IndexItem[]);
  }
  if (!props.availableIds.index) return [];
  return props.availableIds.index.map((id: number) => props.indices[id]);
});
const allItems = computed(() => {
  const nullItems = props.includeNull
    ? [
        {
          type: null,
          id: null
        }
      ]
    : [];
  
  const items: (TargetItem | ResourceItem | IndexItem | {
    type: null;
    id: null;
  })[] = [
    ...nullItems
  ];

  for (const type of props.types) {
    if (type === "target") {
      items.push(...availableTargets.value);
    } else if (type === "resource") {
      items.push(...availableResources.value);
    } else if (type === "index") {
      items.push(...availableIndices.value);
    }
  }

  return items;
});
const disabledAndUnavailableItems = computed(() => {
  const disabledItemUids = new Set(props.disabledItemUids);

  const targets = availableTargets.value;
  const resources = availableResources.value;
  const indices = availableIndices.value;

  if (targets.length == 0) {
    for (const targetId in props.targets) {
      disabledItemUids.add(toUid("target", targetId));
    }
  } else {
    const unavailableTargets = filter(
      props.targets,
      (e) => !targets.some((t) => t.id == e.id)
    );
    for (const target of unavailableTargets) {
      disabledItemUids.add(toUid("target", target.id));
    }
  }

  if (resources.length == 0) {
    for (const resourceId in props.resources) {
      disabledItemUids.add(toUid("resource", resourceId));
    }
  } else {
    const unavailableResources = filter(
      props.resources,
      (e) => !resources.some((r) => r.id == e.id)
    );
    for (const resource of unavailableResources) {
      disabledItemUids.add(toUid("resource", resource.id));
    }
  }

  if (indices.length == 0) {
    for (const indexId in props.indices) {
      disabledItemUids.add(toUid("index", indexId));
    }
  } else {
    const unavailableIndices = filter(
      props.indices,
      (e) => !indices.some((i) => i.id == e.id)
    );
    for (const index of unavailableIndices) {
      disabledItemUids.add(toUid("index", index.id));
    }
  }

  console.log("disabledItemUids", disabledItemUids);

  return disabledItemUids;
});

const allItemsFiltered = computed(() => {
  if (!props.hideSelected) return allItems.value;
  return allItems.value.filter(
    (item) =>
      (item.type == null || !props.selection.includes(item)) &&
      disabledAndUnavailableItems.value.has(toUid(item.type as ElementItem["type"], item.id as number)) === false
  );
});

const choosableTypes = computed(() => {
  const types = [] as ("target" | "resource" | "index")[];
  if (availableTargets.value.length > 0) types.push("target");
  if (availableResources.value.length > 0) types.push("resource");
  if (availableIndices.value.length > 0) types.push("index");
  return types;
});

const instructionText = computed(() => {
  if (props.instruction) {
    return props.instruction;
  }

  if (choosableTypes.value.length === 2) {
    return t("headings.chooseTargetsOrRessources");
  }
  if (choosableTypes.value.includes("target")) {
    return t("actions.selectTargets");
  }
  if (choosableTypes.value.includes("resource")) {
    return t("actions.chooseResources");
  }
  if (choosableTypes.value.includes("index")) {
    return t("actions.chooseIndices");
  }
  return "";
});
  
function onDropdownSelect(item: { uid: Uid<T>; type: T | null }) {
  if (item.type == null) {
    emit("select-single", null);
    return;
  }
  emit("select", [item]);
  emit("select-single", normalizeIdentity({ uid: item.uid }));
}

function onNetChoose(items: { uid: Uid<T> }[]) {
  choosing.value = false;
  emit("select", items);
  emit("choose", items.map((i) => normalizeIdentity({ uid: i.uid })));
}

function focus() {
  if (dropdown.value) dropdown.value.focus();
}

defineExpose({
  focus
});
</script>

<style lang="scss">
.item-combo-select {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 3rem;

  &__dropdown {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right-color: var(--color-text-lighter);
  }
  &__button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-color: transparent;
    font-size: 18px;
  }

  &--no-net {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
